<template>
    <div class="container"
         :class="{'on-hover': isMenuOnHover, 'closed-menu': !isMenuOpen}"
    >
        <div class="menu"
             v-iron-source-click-outside="{
            handler: 'handleClickOutside',
            exclude: ['leftMenu']
         }"
             ref="leftMenu"
             @mouseenter="resetTimerForDelay"
             @mouseleave="delayChooseDefault"
        >
            <Logo :isMenuOpen="isMenuOpen || isMenuOnHover"></Logo>
            <div class="dresser"
                 @mouseleave="changeMenuHoverStatus(false)"
                 @mouseenter="changeMenuHoverStatus(true)">
                <div v-for="section in leftMenuDataList" :key="section.id"
                     :id="`drawer${section.id}`" class="sections"
                     :ref="`drawer${section.id}`"
                     :style="{'max-height': isDrawerOpen(section) ? getDrawerHeight(section.id) : '50px'}"
                     :class="{'active': currentSection === section.id,
                              'drawer-open': isDrawerOpen(section),
                              'hide-on-mobile': section.hideOnMobile}"
                >
                    <div @click="toggleDrawer(section.id)">
                        <Item :hasChildren="section.pageGroups && section.pageGroups.length > 0"
                              :drawerOpen="isDrawerOpen(section)"
                              :displayText="section.name"
                              :actionPath="section.inlineLink"
                              :actionText="section.inlineLinkText"
                              :actionIsExternal="section.actionIsExternal"
                              :hasAction="hasAction(section, currentSection)"
                              :hideOnMobile="section.hideOnMobile"
                              :isSelected="currentSection === section.id"
                              :icon="section.icon"
                              :path="section.path"
                              :isMenuOpen="isMenuOpen"
                              :isHeader="true"
                              :isMenuOnHover="isMenuOnHover"
                              :externalRoute="section.externalRoute"
                              @itemClicked="closeOnSmallScreens(section)"
                        >
                        </Item>
                    </div>
                    <div v-for="(pageGroup, index) in section.pageGroups" :key="pageGroup.id"
                         class="sub-menu groups"
                         :class="{'page-item': !pageGroup.pages,
                                  'hide-on-mobile': pageGroup.hideOnMobile,
                                  'first': index === 0
                              }">
                        <div class="border-top hide-on-mobile" v-if="showBorderTop(section, pageGroup, index)"></div>
                        <Item :hasChildren="pageGroup.pages && pageGroup.pages.length > 0"
                              :drawerOpen="false"
                              :displayText="pageGroup.name"
                              :actionPath="pageGroup.inlineLink"
                              :actionText="pageGroup.inlineLinkText"
                              :actionIsExternal="pageGroup.actionIsExternal"
                              :hasAction="hasAction(pageGroup, currentPage)"
                              :forceOpen="false"
                              :hideOnMobile="!pageGroup.path"
                              :isSelected="pageGroup.id === currentPage"
                              :icon="pageGroup.icon"
                              :path="pageGroup.path"
                              :isMenuOpen="isMenuOpen"
                              :isHeader="false"
                              :isMenuOnHover="isMenuOnHover"
                              :externalRoute="pageGroup.externalRoute"
                              :disableRepeatClick="!pageGroup.enableRepeatClick"
                              @itemClicked="closeOnSmallScreens(pageGroup)"
                        >
                        </Item>
                        <div v-for="page in pageGroup.pages" :key="page.id"
                             class="page-item pages" :class="{'hide-on-mobile': page.hideOnMobile}">
                            <Item :hasChildren="false"
                                  :drawerOpen="false"
                                  :displayText="page.name"
                                  :actionPath="page.inlineLink"
                                  :actionText="page.inlineLinkText"
                                  :actionIsExternal="page.actionIsExternal"
                                  :hasAction="hasAction(page, currentPage)"
                                  :forceOpen="false"
                                  :hideOnMobile="page.hideOnMobile"
                                  :isSelected="page.id === currentPage"
                                  :path="page.path"
                                  :openInNewTab="page.openInNewTab"
                                  :isMenuOpen="isMenuOpen"
                                  :isHeader="false"
                                  :isMenuOnHover="isMenuOnHover"
                                  :externalRoute="page.externalRoute"
                                  :disableRepeatClick="!page.enableRepeatClick"
                                  @itemClicked="closeOnSmallScreens(page)"
                            >
                            </Item>
                        </div>
                    </div>
                </div>
                <div v-show="leftMenuDataList.length > 0" class="kc-link" id="drawerKnowledgeCenter">
                    <IconBase iconName="notificationInfoIcon" :iconColor="consts.COLORS.WHITE"
                              height="16" width="14" class="info-icon"
                              verticalAlign="middle"
                    ></IconBase>
                    <a :href="kcLink" target="_blank" class="link">
                        Knowledge Center
                        <IconBase iconName="newWindowIcon" :iconColor="consts.COLORS.PRIMARY_500"
                                  height="8" width="8" class="open-new-window-menu"
                        ></IconBase>
                    </a>
                </div>
                <User class="show-on-mobile"
                      mode="mobile"
                      v-if="isMobileView"
                      @itemClicked="changeMenuToggle(false)">
                </User>
                <div class="partial-support">*Partial Support for Mobile</div>
            </div>
        </div>
    </div>
</template>

<script>
import IconBase from 'components/IconBase.vue';
import consts from 'services/metaData/consts';
import links from 'services/metaData/links';
import Item from 'components/layout/leftMenu/Item.vue';
import { encode, decode } from 'services/utils/base64';
import auth from 'services/auth';
import ironSourceClickOutside from 'directives/IronSourceClickOutside';
import { mapMutations, mapGetters } from 'vuex';
import Logo from 'components/layout/leftMenu/Logo.vue';
import {
    openAllWidth, startClosedWidth, startOverlayWidth, mobileWidth
} from 'components/layout/leftMenu/consts';
import User from 'components/layout/user/User.vue';

export default {
    name: 'LeftMenu',
    components: {
        User,
        Logo,
        Item,
        IconBase
    },
    directives: { ironSourceClickOutside },
    props: {
        leftMenuDataList: {
            type: Array,
            default: () => [],
            required: false
        }
    },
    data() {
        return {
            consts,
            kcLink: links.knowledgeCenter,
            allOpenMode: false, // all the drawers are always open
            openDrawer: false,
            timeout: null,
            unsubscribeStore: null
        };
    },
    computed: {
        ...mapGetters([
            'currentPage',
            'currentSection',
            'isMenuOpen',
            'isMenuOnHover',
            'windowWidth',
            'isMobileView'
        ])
    },
    watch: {
        leftMenuDataList(newVal) {
            if (newVal) {
                this.openDrawer = false;
                this.$nextTick().then((() => {
                    this.chooseDefaultDrawer();
                    // eslint-disable-next-line no-extra-bind
                }).bind(this));
            }
        }
    },
    methods: {
        changeMenuHoverStatus(status) {
            // hover is not relevant when the menu is open
            if (this.isMenuOpen || this.allOpenMode) {
                return;
            }

            // change hover status
            this.setIsMenuOnHover(status);

            // open/close drawers
            if (!this.isMenuOnHover) {
                this.openDrawer = false;
            } else {
                this.chooseDefaultDrawer();
            }
        },
        chooseDefaultDrawer() {
            if (this.openDrawer !== this.currentSection) {
                this.toggleDrawer(this.currentSection);
            }
        },
        toggleDrawer(sectionId) {
            // when the menu is closed we don't allow to open drawers, they are shown in hover
            if ((!this.isMenuOpen && !this.isMenuOnHover) || this.allOpenMode) {
                return;
            }
            this.openDrawer = this.openDrawer === sectionId ? false : sectionId;
        },
        changeMenuToggle(forcedState) {
            this.setIsMenuOpen(forcedState);
        },
        isDrawerOpen(section) {
            return this.allOpenMode
                || ((this.isMenuOpen || this.isMenuOnHover)
                    && section.pageGroups && section.pageGroups.length > 0 && this.openDrawer === section.id);
        },
        handleClickOutside() {
            if (this.isMenuOpen && this.windowWidth < startOverlayWidth && this.windowWidth > mobileWidth) {
                this.changeMenuToggle(false);
            }
        },
        calcAllOpenMode() {
            this.allOpenMode = this.windowWidth < openAllWidth;
        },
        onResize() {
            this.calcAllOpenMode();
        },
        resetTimerForDelay() {
            if (this.timeout) {
                clearTimeout(this.timeout);
                this.timeout = null;
            }
        },
        delayChooseDefault() {
            if (!this.timeout) {
                this.timeout = setTimeout(() => {
                    this.chooseDefaultDrawer();
                    clearTimeout(this.timeout);
                    this.timeout = null;
                }, 2000);
            }
        },
        getDrawerHeight(sectionId) {
            let height = '50px';
            const key = `drawer${sectionId}`;
            if (this.$refs[key] && this.$refs[key][0]) {
                const drawerElement = this.$refs[key][0];
                if (drawerElement.scrollHeight > 50) {
                    height = `${drawerElement.scrollHeight}px`;
                }
                if (drawerElement.scrollHeight === 0) {
                    height = 'initial';
                }
            }
            return height;
        },
        showBorderTop(section, pageGroup, index) {
            return pageGroup.pages && pageGroup.pages.length > 0
                && index !== 0 && section.pageGroups[index - 1].pages
                && section.pageGroups[index - 1].pages.length > 0;
        },
        closeOnSmallScreens(pageItem) {
            if (pageItem.path && this.isMenuOpen && this.windowWidth < startOverlayWidth) {
                this.changeMenuToggle(false);
            }
        },
        hasAction(page, current) {
            return page.inlineLinkAuthorized && page.inlineLink && page.id !== current;
        },
        ...mapMutations([
            'setCurrentPage',
            'setCurrentSection',
            'setIsMenuOpen',
            'setIsMenuOnHover'
        ])
    },
    mounted() {
        window.addEventListener('resize', this.onResize);
        this.chooseDefaultDrawer();
        this.calcAllOpenMode();
    },
    created() {
        const defaultCollapsedState = sessionStorage.getItem('menuCollapsed');
        if (defaultCollapsedState && this.windowWidth > mobileWidth) {
            this.setIsMenuOpen(!JSON.parse(decode(defaultCollapsedState)));
        } else if (this.windowWidth < startClosedWidth) {
            this.setIsMenuOpen(false);
        }
        const defaultSelectedPage = sessionStorage.getItem('menuSelected');
        if (defaultSelectedPage) {
            const preSelectedData = defaultSelectedPage.split(':');
            if (preSelectedData.length === 3) {
                this.setCurrentSection(preSelectedData[0]);
                this.setCurrentPage(preSelectedData[2]);
            }
            sessionStorage.removeItem('menuSelected');
        }

        this.unsubscribeStore = this.$store.subscribe((mutation) => {
            if (mutation.type === 'setIsMenuOpen') {
                if (!mutation.payload) {
                    this.openDrawer = false;
                } else {
                    this.$nextTick(() => {
                        this.chooseDefaultDrawer();
                    });
                }

                // store menu state for using in DSI 2.0 (demand-platform)
                sessionStorage.setItem('menuCollapsed', encode(JSON.stringify(!mutation.payload)));
            } else if (mutation.type === 'setCurrentPageAndSection') {
                this.delayChooseDefault();
            }
        });
    },
    beforeDestroy() {
        if (this.unsubscribeStore) {
            this.unsubscribeStore();
        }
        window.removeEventListener('resize', this.onResize);
    }
};
</script>

<style scoped lang="scss">
@import "../common";
@import "~styles/functions";

$partialDisclaimerFontColor: $light-800;
$subHeaderFontColor: $light-800;
$menuShadow: 3px 0 5px 0 rgba(0, 0, 0, 0.3);

.container {
    float: left;
    width: $mainMenuWidth;
    display: inline-block;
    vertical-align: middle;
    height: 100vh;
    padding: 0;
    transition: width $menuAnimations;
    z-index: 1;

    &.closed-menu {
        width: $mainMenuClosedWidth;

        @media #{$tabletAndBelow} {
            width: 0;
        }
    }

    @media #{$tabletAndBelow} {
        width: 0;
    }
}

.closed-menu .menu {
    width: $mainMenuClosedWidth;

    @media #{$tabletAndBelow} {
        display: none;
    }
}
.closed-menu.on-hover .menu{
    width: $mainMenuWidth;
}
.menu {
    width: $mainMenuWidth;
    height: 100vh;
    background-color: $menuBackground;
    overflow: auto;
    position: absolute;

    @media #{$tabletAndBelow} {
        box-shadow: $menuShadow;
    }
    @media #{$mobile} {
        height: auto;
        background-color: $activeMenu;
        max-height: calc(100vh - 50px);
        width: 100vw;
        top: 50px;
    }

    z-index: getZIndexLayerOffset(application,900);
    transition: width $menuAnimations;
}

.closed-menu .dresser {
    min-height: calc(100vh - #{$calculatedHeaderHeight});
    @media #{$laptop} {
        min-height: calc(100vh - #{$calculatedHeaderHeightMiniMode});
    }

    @media #{$tablet} {
        min-height: calc(100vh - #{$calculatedHeaderHeightTitleMode});
    }

    @media #{$mobile} {
        min-height: initial;
    }
}

.closed-menu.on-hover .dresser {
    box-shadow: $menuShadow;
}

.dresser {
    color: $menuItemFontColor;
    line-height: 32px;
}

.sub-menu {
    margin-left: remCalc(18px);
    padding-bottom: remCalc(7px);
    margin-right: remCalc(15px);
    padding-top: remCalc(1px);

    &.first {
        margin-top: -8px;
    }
    &.groups.page-item {
        @media  #{$mobile} {
            margin-left: remCalc(32px);
        }
    }

    &.page-item {
        white-space: nowrap;
        border-top: none;
        padding-bottom: 0;
        line-height: 15px;
        position: relative;

        &:first-child, &.first{
            padding-top: 0;
        }
        &:last-child{
            padding-bottom: 18px;
        }

        @media  #{$mobile}{
            padding-bottom: 7px;
            border-top: none;
            &:first-child{
                padding-top: 10px;
            }
        }
    }

    @media  #{$mobile}{
        border-top: none;
        padding-top: 0;
        margin-top: -10px;
    }
}

.pages {
    margin-left: remCalc(14px);
}

.sub-header{
    @include font(8);
    color: $subHeaderFontColor;

    @media #{$mobile}{
        display: none
    }
}

.sub-title{
    @include font(7);
}

.partial-support {
    @include font(7);
    display: none;
    color: $partialDisclaimerFontColor;
    background-color: $white;
    line-height: 30px;
    text-align: center;

    @media #{$mobile} {
        display: block;
    }
}

.closed-menu .kc-link {
    .link {
        opacity: 0;
    }
}
.closed-menu.on-hover .kc-link{
    .link {
        opacity: 1;
    }
}
.kc-link {
    line-height: $sectionHeight;
    max-height: $sectionHeight;
    @include font(4);
    padding-left: remCalc(26px);
    overflow: hidden;
    position: relative;

    .info-icon{
        margin-right: 11px;
        ::v-deep svg {
            margin-top: -2px;
        }
    }

    .link {
        text-decoration: none;
        color: $primary-500;
        transition: opacity $menuAnimations;
        display: inline-block;
        padding-left: 3px;

        &:hover, &:active {
            text-decoration: none;
            color: $primary-500;
        }
    }

    .open-new-window-menu {
        margin-left: 3px;
        vertical-align: baseline;
    }
}

.sections {
    padding-left: remCalc(21px);
    border-left: solid 5px transparent;
    position: relative;

    // animation
    transition: max-height $menuAnimations;
    overflow: hidden;
    // end animation

    &:hover{
        background-color: $activeMenu;
    }

    @media #{$mobile}{
        border-bottom: 1px solid $light-900;
    }

    &.active {
        border-left: solid 5px $selectedColor;
        background-color: $activeMenu;
    }
    &.drawer-open {
        background-color: $activeMenu;
    }
}
.border-top {
    border-top: solid 1px $light-900;
    margin-top: 5px;
    margin-bottom: 5px;
    margin-left: 10px
}

</style>
